/**
 * @krisspy-file
 * @type page
 * @name "DiscountCodesList"
 * @title "Discount Codes"
 * @description "Manage discount codes and promotional offers"
 * @routes ["/discount-codes"]
 * @flowName "discount-management"
 * @layout "DashboardLayout"
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  Filter,
  X,
  Loader2,
  Ticket,
  Percent,
  DollarSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { getDiscountCodes, type DiscountCode } from '../services/discountCodes.service';
import { getMerchantById } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDateFormatter } from '../hooks/useDateFormatter';

type FilterStatus = 'all' | 'active' | 'inactive';
type FilterType = 'all' | 'percentage' | 'fixed_amount' | 'timecard';

export default function DiscountCodesList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const { showToast } = useToast();
  const { formatDate } = useDateFormatter();

  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currency, setCurrency] = useState<string>('EUR');

  useEffect(() => {
    loadCodes();
  }, [filterStatus, filterType]);

  async function loadCodes() {
    setLoading(true);

    // Load merchant currency
    if (merchantId) {
      const { data: merchant } = await getMerchantById(merchantId);
      if (merchant?.currency_code) {
        setCurrency(merchant.currency_code);
      }
    }

    const params: any = {
      merchantId,
      locationIds,
      isPlatformAdmin
    };

    if (filterStatus !== 'all') {
      params.active = filterStatus === 'active';
    }

    if (filterType !== 'all') {
      params.discountType = filterType;
    }

    const { data, error } = await getDiscountCodes(params);

    if (error) {
      showToast('Failed to load discount codes: ' + error, 'error');
    } else if (data) {
      setCodes(data);
    }

    setLoading(false);
  }

  function handleCodeClick(codeId: string) {
    navigate(`/discount-codes/${codeId}/edit`);
  }

  function handleCreateCode() {
    navigate('/discount-codes/create');
  }

  function getDiscountTypeIcon(type: string) {
    switch (type) {
      case 'percentage':
        return <Percent className="w-4 h-4" />;
      case 'fixed_amount':
        return <DollarSign className="w-4 h-4" />;
      case 'timecard':
        return <CreditCard className="w-4 h-4" />;
      default:
        return <Ticket className="w-4 h-4" />;
    }
  }

  function getDiscountTypeLabel(type: string) {
    switch (type) {
      case 'percentage':
        return t('discountCodes.percentage');
      case 'fixed_amount':
        return t('discountCodes.fixedAmount');
      case 'timecard':
        return t('discountCodes.timecard');
      default:
        return type;
    }
  }

  function formatDiscountValue(code: DiscountCode) {
    const val = code.discount_value ?? 0;
    if (code.discount_type === 'percentage') {
      return `${val}%`;
    } else if (code.discount_type === 'timecard') {
      const remaining = val - (code.current_value_used ?? 0);
      return `${currency} ${remaining.toFixed(2)} / ${currency} ${val.toFixed(2)}`;
    } else {
      return `${currency} ${val.toFixed(2)}`;
    }
  }

  function isExpired(code: DiscountCode) {
    if (!code.valid_to) return false;
    return new Date(code.valid_to) < new Date();
  }

  function isUsedUp(code: DiscountCode) {
    if (code.discount_type === 'timecard') {
      return (code.current_value_used ?? 0) >= code.discount_value;
    }
    if (code.max_uses) {
      return (code.current_uses ?? 0) >= code.max_uses;
    }
    return false;
  }

  function formatValidityDates(code: DiscountCode) {
    if (!code.valid_from && !code.valid_to) return t('discountCodes.noExpiry');

    const from = code.valid_from ? formatDate(code.valid_from) : 'Any';
    const to = code.valid_to ? formatDate(code.valid_to) : 'Any';

    return `${from} - ${to}`;
  }

  // Compact "17:00–19:00 · Mon, Fri" summary of a code's recurring daily
  // time window and ISO weekdays (1 = Monday … 7 = Sunday), or null when the
  // code has neither.
  function formatRecurringWindow(code: DiscountCode) {
    const parts: string[] = [];

    if (code.valid_time_from || code.valid_time_until) {
      // TIME columns come back as 'HH:MM:SS'
      const from = code.valid_time_from ? code.valid_time_from.slice(0, 5) : '';
      const until = code.valid_time_until ? code.valid_time_until.slice(0, 5) : '';
      parts.push(`${from}–${until}`);
    }

    if (code.valid_days_of_week && code.valid_days_of_week.length > 0) {
      const dayKeys = ['dayMon', 'dayTue', 'dayWed', 'dayThu', 'dayFri', 'daySat', 'daySun'];
      parts.push(
        code.valid_days_of_week
          .filter(d => d >= 1 && d <= 7)
          .map(d => t(`discountCodes.${dayKeys[d - 1]}`))
          .join(', ')
      );
    }

    return parts.length > 0 ? parts.join(' · ') : null;
  }

  function formatUsageStats(code: DiscountCode) {
    const uses = code.current_uses ?? 0;
    if (code.discount_type === 'timecard') {
      return `${uses} ${t('discountCodes.uses')}`;
    }
    if (code.max_uses) {
      return `${uses} / ${code.max_uses}`;
    }
    return `${uses} ${t('discountCodes.uses')}`;
  }

  const filteredCodes = codes.filter(code => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      code.code.toLowerCase().includes(search) ||
      (code.description && code.description.toLowerCase().includes(search))
    );
  });

  const activeFiltersCount =
    (filterStatus !== 'all' ? 1 : 0) +
    (filterType !== 'all' ? 1 : 0);

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="card">
          <div className="flex items-center justify-center py-2xl">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="ml-md text-secondary">{t('discountCodes.loadingCodes')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-md">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary mb-sm">{t('discountCodes.title')}</h1>
          <p className="text-sm sm:text-base text-secondary">
            {t('discountCodes.description')}
          </p>
        </div>
        <button onClick={handleCreateCode} className="btn btn-primary">
          <Plus className="w-4 h-4" />
          {t('discountCodes.createCode')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-md">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
            <input
              type="text"
              placeholder={t('discountCodes.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${activeFiltersCount > 0 ? 'btn-primary' : 'btn-ghost'}`}
          >
            <Filter className="w-4 h-4" />
            {t('common.filter')}
            {activeFiltersCount > 0 && (
              <span className="ml-xs px-xs py-0.5 bg-white text-blue rounded-full text-xs font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-md pt-md border-t border-primary">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">{t('discountCodes.status')}</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                  className="input w-full"
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="active">{t('common.active')}</option>
                  <option value="inactive">{t('common.inactive')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">{t('discountCodes.type')}</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as FilterType)}
                  className="input w-full"
                >
                  <option value="all">{t('discountCodes.allTypes')}</option>
                  <option value="percentage">{t('discountCodes.percentage')}</option>
                  <option value="fixed_amount">{t('discountCodes.fixedAmount')}</option>
                  <option value="timecard">{t('discountCodes.timecard')}</option>
                </select>
              </div>
            </div>

            {activeFiltersCount > 0 && (
              <button
                onClick={() => {
                  setFilterStatus('all');
                  setFilterType('all');
                }}
                className="btn btn-ghost mt-md"
              >
                <X className="w-4 h-4" />
                {t('discountCodes.clearFilters')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Codes List */}
      <div className="card p-0 overflow-hidden">
        {filteredCodes.length === 0 ? (
          <div className="text-center py-2xl">
            <Ticket className="w-16 h-16 text-secondary mx-auto mb-md" />
            <h3 className="text-lg font-semibold text-primary mb-sm">
              {searchTerm ? t('discountCodes.noCodesFound') : t('discountCodes.noCodesYet')}
            </h3>
            <p className="text-secondary mb-lg">
              {searchTerm
                ? t('discountCodes.tryAdjusting')
                : t('discountCodes.noCodes')}
            </p>
            {!searchTerm && (
              <button onClick={handleCreateCode} className="btn btn-primary">
                <Plus className="w-4 h-4" />
                {t('discountCodes.createFirstCode')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary border-b border-primary">
                <tr>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.code')}
                  </th>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.type')}
                  </th>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.value')}
                  </th>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.validity')}
                  </th>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.usage')}
                  </th>
                  <th className="text-left px-md py-sm text-xs font-semibold text-secondary uppercase tracking-wider">
                    {t('discountCodes.status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-primary">
                {filteredCodes.map((code) => {
                  const expired = isExpired(code);
                  const usedUp = isUsedUp(code);

                  return (
                    <tr
                      key={code.id}
                      onClick={() => handleCodeClick(code.id)}
                      className="hover:bg-secondary cursor-pointer transition-colors"
                    >
                      <td className="px-md py-md">
                        <div>
                          <p className="font-semibold text-primary font-mono">{code.code}</p>
                          {code.description && (
                            <p className="text-xs text-secondary mt-xs">{code.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-md py-md">
                        <div className="flex items-center gap-xs text-secondary">
                          {getDiscountTypeIcon(code.discount_type)}
                          <span className="text-sm">{getDiscountTypeLabel(code.discount_type)}</span>
                        </div>
                      </td>
                      <td className="px-md py-md">
                        <p className="text-sm font-medium text-primary">{formatDiscountValue(code)}</p>
                      </td>
                      <td className="px-md py-md">
                        <div className="flex items-center gap-xs text-sm text-secondary">
                          <Clock className="w-4 h-4" />
                          {formatValidityDates(code)}
                        </div>
                        {formatRecurringWindow(code) && (
                          <p className="text-xs text-secondary mt-xs">{formatRecurringWindow(code)}</p>
                        )}
                      </td>
                      <td className="px-md py-md">
                        <p className="text-sm text-secondary">{formatUsageStats(code)}</p>
                      </td>
                      <td className="px-md py-md">
                        <div className="flex flex-col gap-xs">
                          {code.active && !expired && !usedUp && (
                            <span className="inline-flex items-center gap-xs px-sm py-xs text-xs bg-green-100 text-green-800 rounded w-fit">
                              <CheckCircle className="w-3 h-3" />
                              {t('common.active')}
                            </span>
                          )}
                          {!code.active && (
                            <span className="inline-flex items-center gap-xs px-sm py-xs text-xs bg-gray-200 text-gray-700 rounded w-fit">
                              <X className="w-3 h-3" />
                              {t('common.inactive')}
                            </span>
                          )}
                          {expired && (
                            <span className="inline-flex items-center gap-xs px-sm py-xs text-xs bg-red-100 text-red-800 rounded w-fit">
                              <AlertCircle className="w-3 h-3" />
                              {t('discountCodes.expired')}
                            </span>
                          )}
                          {usedUp && (
                            <span className="inline-flex items-center gap-xs px-sm py-xs text-xs bg-amber-100 text-amber-800 rounded w-fit">
                              <AlertCircle className="w-3 h-3" />
                              {t('discountCodes.usedUp')}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {filteredCodes.length > 0 && (
        <div className="text-sm text-secondary text-center">
          {t('discountCodes.showingCount', { showing: filteredCodes.length, total: codes.length })}
        </div>
      )}
    </div>
  );
}
